'use client'

import { LockKeyhole } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { getUpgradePlanConfig, resolveUpgradePlanPath } from '@/constants/upgrade-plan'
import { cn } from '@/lib/utils'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'

import {
  buildGatingDescription,
  buildGatingHeadline,
  featureLabelFromConfig,
  planDisplayNameFromConfig,
} from './gating-copy'
import type { EntitlementDeniedPayload, GatingQuotaState } from './types'

export type UpgradeNoticeProps = {
  denial: EntitlementDeniedPayload
  planName?: string
  featureLabel?: string
  description?: string
  quota?: GatingQuotaState
  className?: string
  onUpgradeClick?: () => void
}

/** T3 inline notice — promotion accent, not destructive error styling. */
export function UpgradeNotice({
  denial,
  planName,
  featureLabel,
  description,
  quota,
  className,
  onUpgradeClick,
}: UpgradeNoticeProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const upgrade = getUpgradePlanConfig()
  const resolvedPlanName = planName ?? planDisplayNameFromConfig('paid_1')
  const resolvedFeatureLabel =
    featureLabel ?? featureLabelFromConfig(denial.feature_key)
  const billingPath =
    denial.upgrade_path ||
    resolveUpgradePlanPath(denial.organisation_slug || null)

  const headline = buildGatingHeadline({
    featureKey: denial.feature_key,
    featureLabel: resolvedFeatureLabel,
    planName: resolvedPlanName,
    quota,
    description,
  })
  const body =
    denial.reason?.trim() ||
    buildGatingDescription({
      featureKey: denial.feature_key,
      featureLabel: resolvedFeatureLabel,
      planName: resolvedPlanName,
      quota,
      description,
    })

  const handleUpgrade = () => {
    onUpgradeClick?.()
    navigateWithThreadPreservation(navigate, billingPath, searchParams)
  }

  return (
    <div
      role="status"
      className={cn(
        'rounded-md border border-promotion/30 bg-promotion/10 px-3 py-2 text-sm text-foreground space-y-2',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-promotion" aria-hidden />
        <div className="space-y-1 min-w-0">
          <p className="font-medium leading-snug">{headline}</p>
          <p className="text-muted-foreground leading-snug">{body}</p>
        </div>
      </div>
      {upgrade ? (
        <Button type="button" size="sm" className="w-full sm:w-auto" onClick={handleUpgrade}>
          {upgrade.ctaButtonLabel ?? 'Upgrade plan'}
        </Button>
      ) : (
        <Link
          to={billingPath}
          className="inline-block font-medium text-primary underline underline-offset-2"
        >
          Open Plan & Billing
        </Link>
      )}
    </div>
  )
}
